'use client';

import { Badge, Button, Icon } from '@/components';
import { cn } from '@/libs';
import { type PromotionOffer, formatCurrency, offerPrice, ticketLabels } from '@/utils';

type PromotionPacksProps = {
  offers: PromotionOffer[];
  ticketPrice: number;
  onSelectPack: (offer: PromotionOffer) => void;
};

export const PromotionPacks = ({
  offers,
  ticketPrice,
  onSelectPack,
}: PromotionPacksProps) => {
  if (offers.length === 0) return null;

  return (
    <div data-cy="promotion-packs" className="w-full space-y-3 md:space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-foreground text-lg font-semibold">Super Combos</h3>
        <Icon.Fire width={16} height={16} className="text-secondary" />
      </div>

      <div className="grid grid-cols-3 gap-0.5 md:gap-1">
        {offers.map((offer, index) => {
          const discountedPrice = offerPrice({
            quantity: offer.quantity,
            unitPrice: ticketPrice,
            percentage: offer.percentage,
          });

          return (
            <Button
              key={offer.id}
              data-cy={`promotion-pack-${index}-button`}
              variant="ghost"
              onClick={() => onSelectPack(offer)}
              className={cn(
                'relative p-3 h-12 md:h-18 rounded-lg border-2 transition-all',
                offer.highlight
                  ? 'animate-pulse duration-1000 transform scale-105 bg-primary/10 border-primary text-foreground'
                  : 'border-primary/10 bg-background hover:border-foreground/80 text-foreground',
              )}
            >
              {offer.percentage > 0 && (
                <Badge
                  text={`${offer.percentage}% OFF`}
                  className="absolute -top-3 -right-2 bg-badge text-foreground"
                />
              )}

              <span className="text-foreground text-xs md:text-base">
                {offer.quantity} {ticketLabels.pluralCaps}{' '}
                {formatCurrency(discountedPrice)}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
